import { useSyncExternalStore } from 'react';

/**
 * The width at which every top-bar link fits on one line - the mobile menu,
 * 29 September 2026.
 *
 * Tailwind's `lg`, 1024px. Measured rather than guessed: the operator's nine
 * links with the wordmark need about 860px, the agency's eight about 790px,
 * and the content column is 1024px less its gutters. Below this the links go
 * behind the Menu button.
 */
export const WIDE_NAV_QUERY = '(min-width: 1024px)';

/**
 * Whether a media query matches, kept current as the window changes.
 *
 * **Decided in script, not only in CSS**, so the narrow and wide bars are two
 * different trees rather than one tree with half of it hidden. That is what lets
 * a test say "at a phone width the links are not on the page", which a `hidden`
 * class could never show in a DOM with no stylesheet.
 *
 * **Where `matchMedia` does not exist it answers `fallback`.** jsdom has none,
 * so every existing test keeps the wide bar it was written against, and a test
 * that wants the phone installs a `matchMedia` of its own.
 */
export function useMediaQuery(query: string, fallback = true): boolean {
  return useSyncExternalStore(
    (onChange) => {
      if (typeof window.matchMedia !== 'function') return () => undefined;
      const list = window.matchMedia(query);
      list.addEventListener('change', onChange);
      return () => {
        list.removeEventListener('change', onChange);
      };
    },
    () => (typeof window.matchMedia === 'function' ? window.matchMedia(query).matches : fallback),
    () => fallback,
  );
}
